import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserPlus, UserMinus } from 'lucide-react';
import { FieldHelp } from '@/features/guided-help';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';

export interface PlayersPerTeamFieldProps {
  value: number;
  onChange: (value: number) => void;
  minPlayers: number;
  maxPlayers: number;
  /**
   * 'singlesDoubles' — Singles/Doubles labels (tennis, squash doubles).
   * 'jugadores' — "player(s) per team" (padel).
   */
  labelStyle?: 'singlesDoubles' | 'jugadores';
  cardTitle?: string;
  wrapInCard?: boolean;
  disabled?: boolean;
  /** Helper text below the control (e.g. tennis: singles vs doubles). */
  footerHint?: string;
}

export function PlayersPerTeamField({
  value,
  onChange,
  minPlayers,
  maxPlayers,
  labelStyle = 'jugadores',
  cardTitle = 'Jugadores por equipo',
  wrapInCard = true,
  disabled = false,
  footerHint,
}: PlayersPerTeamFieldProps) {
  const clamped = Math.max(minPlayers, Math.min(value, maxPlayers));

  const handleChange = (next: number) => {
    if (disabled) return;
    const v = Math.max(minPlayers, Math.min(next, maxPlayers));
    onChange(v);
  };

  const isFixed = minPlayers === maxPlayers;

  const subtitle =
    labelStyle === 'singlesDoubles'
      ? clamped === 1
        ? 'Singles'
        : 'Dobles'
      : clamped === 1
        ? 'jugador por equipo'
        : 'jugadores por equipo';

  const fixedCaption =
    labelStyle === 'singlesDoubles'
      ? clamped === 1
        ? 'Singles'
        : 'Dobles'
      : minPlayers === 2 && maxPlayers === 2
        ? 'jugadores por equipo (fijo para Pádel)'
        : subtitle;

  const inner = (
    <>
      {isFixed ? (
        <div className="flex flex-col items-center justify-center py-4">
          <span className="text-4xl font-bold text-blue-600">{clamped}</span>
          <p className="mt-2 text-sm text-gray-500">{fixedCaption}</p>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => handleChange(clamped - 1)}
            disabled={disabled || clamped <= minPlayers}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <UserMinus className="h-4 w-4" />
          </button>

          <div className="flex-1 text-center">
            <span className="text-3xl font-bold text-gray-900">{clamped}</span>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>

          <button
            type="button"
            onClick={() => handleChange(clamped + 1)}
            disabled={disabled || clamped >= maxPlayers}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <UserPlus className="h-4 w-4" />
          </button>
        </div>
      )}

      {footerHint ? (
        <p className="mt-4 text-center text-xs text-gray-500">{footerHint}</p>
      ) : labelStyle === 'singlesDoubles' && !isFixed ? (
        <p className="mt-4 text-center text-xs text-gray-500">
          {clamped === 1
            ? 'Torneo individual (Singles)'
            : 'Torneo de parejas (Dobles)'}
        </p>
      ) : null}
    </>
  );

  if (!wrapInCard) {
    return (
      <div
        className={wizardBlockShellClassName}
        data-guide-id="wizard.team.playersPerTeam"
      >
        {inner}
      </div>
    );
  }

  return (
    <Card
      className={wizardBlockCardClassName}
      data-guide-id="wizard.team.playersPerTeam"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserPlus className="h-5 w-5" />
          {cardTitle}
          <FieldHelp topicId="wizard.team.playersPerTeam" />
        </CardTitle>
      </CardHeader>
      <CardContent>{inner}</CardContent>
    </Card>
  );
}
